"use client"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useMemo } from "react"
import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput } from "@/components/form-inputs"
import { CreditLifetimeNote } from "@/components/billing/credit-lifetime-note"
import { useRequestPayoutMutation, useTransferToWalletMutation } from "@/hooks/mutations/partners"
import { formatPercent } from "@/lib/partners/percent"

import { formatMicroUsd, parseUsdToMicroUsd } from "@/lib/billing/utils/format"
import { computeCashPayoutFee } from "@/lib/partners/payout-fee"
import { useTranslations } from "next-intl"




type AmountForm = { amount: string }

export function TransferToWalletDialog({
  open,
  balanceMicrousd,
  creditLifetimeMonths,
  onOpenChange,
}: {
  open: boolean
  balanceMicrousd: number
  /** null = the transferred credits never expire. */
  creditLifetimeMonths: number | null
  onOpenChange: (open: boolean) => void
}) {
  const { formatCredits } = useCreditFormatters()



  const tUi = useTranslations("Partners.PartnersPartnerPayoutDialogs")

  const mutation = useTransferToWalletMutation()
  // One reference per dialog open: a double submit then replays instead of
  // transferring twice (the RPC is idempotent on it).
  const reference = useMemo(() => (open ? crypto.randomUUID() : ""), [open])

  const formik = useFormik<AmountForm>({
    initialValues: { amount: "" },
    validate: values => validateAmount(values, { max: balanceMicrousd }),
    onSubmit: async values => {
      try {
        await mutation.mutateAsync({ amount_microusd: toMicrousd(values.amount) ?? 0, reference })
        formik.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  const previewMicrousd = toMicrousd(formik.values.amount)

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("transferToWallet")}>{tUi("transferToWallet")}</span></DialogTitle>
              <DialogDescription>
                {<span key={tUi("movesEarningsToYourWorkspaceWalletWhereThey", { value0: String(formatMicroUsd(balanceMicrousd)) })}>{tUi("movesEarningsToYourWorkspaceWalletWhereThey", { value0: String(formatMicroUsd(balanceMicrousd)) })}</span>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-5">
              <FormInput name="amount" label={tUi("amountUSD")} inputMode="decimal" autoFocus />
              {previewMicrousd !== null && previewMicrousd > 0 && (
                <dl className="text-sm">
                  <PreviewRow label={tUi("youReceive")} value={formatCredits(previewMicrousd)} emphasized />
                </dl>
              )}
              <CreditLifetimeNote months={creditLifetimeMonths} subject="Transferred credits" className="text-xs text-muted-foreground" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("transfer")}>{tUi("transfer")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

export function RequestPayoutDialog({
  open,
  balanceMicrousd,
  minPayoutMicrousd,
  feePercent,
  onOpenChange,
}: {
  open: boolean
  balanceMicrousd: number
  minPayoutMicrousd: number
  feePercent: number
  onOpenChange: (open: boolean) => void
}) {


  const tUi = useTranslations("Partners.PartnersPartnerPayoutDialogs")

  const mutation = useRequestPayoutMutation()
  // Doubles as the request id: a lost response replays instead of holding twice. A refusal writes nothing,
  // so the dialog keeps it for the confirmation at the new fee.
  const reference = useMemo(() => (open ? crypto.randomUUID() : ""), [open])

  const formik = useFormik<AmountForm>({
    initialValues: { amount: "" },
    validate: values => validateAmount(values, { max: balanceMicrousd, min: minPayoutMicrousd }),
    onSubmit: async values => {
      const amountMicrousd = toMicrousd(values.amount) ?? 0
      try {
        await mutation.mutateAsync({
          amount_microusd: amountMicrousd,
          reference,
          expected_fee_microusd: computeCashPayoutFee(amountMicrousd, feePercent),
        })
        formik.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  const requestedMicrousd = toMicrousd(formik.values.amount)
  const feeMicrousd = requestedMicrousd === null ? 0 : computeCashPayoutFee(requestedMicrousd, feePercent)

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("requestAPayout")}>{tUi("requestAPayout")}</span></DialogTitle>
              <DialogDescription>
                {<span key={tUi("theAmountLeavesYourPartnerBalanceRightAway", { value0: String(formatMicroUsd(minPayoutMicrousd)), value1: String(formatMicroUsd(balanceMicrousd)) })}>{tUi("theAmountLeavesYourPartnerBalanceRightAway", { value0: String(formatMicroUsd(minPayoutMicrousd)), value1: String(formatMicroUsd(balanceMicrousd)) })}</span>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-5">
              <FormInput name="amount" label={tUi("amountUSD")} inputMode="decimal" autoFocus />
              {requestedMicrousd !== null && requestedMicrousd > 0 && (
                <dl className="space-y-2 text-sm">
                  <PreviewRow label={tUi("requested")} value={formatMicroUsd(requestedMicrousd)} />
                  <PreviewRow label={tUi("feeValue0", { value0: String(formatPercent(feePercent)) })} value={formatMicroUsd(feeMicrousd)} />
                  <PreviewRow label={tUi("youReceive")} value={formatMicroUsd(requestedMicrousd - feeMicrousd)} emphasized />
                </dl>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("requestPayout")}>{tUi("requestPayout")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function PreviewRow({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) {
  return (
    <div className={emphasized ? "flex justify-between gap-4 border-t pt-2" : "flex justify-between gap-4"}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={emphasized ? "whitespace-nowrap font-semibold" : "whitespace-nowrap"}>{value}</dd>
    </div>
  )
}

function validateAmount(values: AmountForm, bounds: { max: number; min?: number }) {
  const microusd = toMicrousd(values.amount)

  if (microusd === null || microusd <= 0) {
    return { amount: "Enter an amount." }
  }
  if (bounds.min !== undefined && microusd < bounds.min) {
    return { amount: `The minimum is ${formatMicroUsd(bounds.min)}.` }
  }
  if (microusd > bounds.max) {
    return { amount: `You only have ${formatMicroUsd(bounds.max)} available.` }
  }
  return {}
}

function toMicrousd(amount: string): number | null {
  return parseUsdToMicroUsd(amount.replace(",", "."))
}
